import { useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import DateTimePicker from 'react-datetime';
import { LevelRewardInputType } from '../../../../../../__gqltypes__';
import Chip from '../../../components/Chip';
import {
  LOYALTY_CAMPAIGN_ITEM_CATEGORY_LABELS,
  LoyaltyCampaignCardKind,
  LoyaltyCampaignCardType,
  LoyaltyCampaignClaimMode,
  LoyaltyCampaignItemCategory,
  LoyaltyCampaignOfferOptionRow,
  RewardFormInput,
  VanityItemType,
  VANITY_ITEM_LABELS,
} from '../types';
import {
  fromRewardFormToLevelRewardInput,
  formatOfferOptionLabel,
  getItemCategoryBadgeStyle,
  getItemCategoryFromRewardForm,
  getRewardLabel,
  getVanityItemTypeFromRewardForm,
  toPickerDate,
} from '../utils';

const modalBodyStyle = {
  background: 'var(--loyalty-surface-alt)',
};

const sectionTitleStyle = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: 'var(--loyalty-text-muted)',
  marginBottom: 14,
};

const segmentedWrapStyle = {
  display: 'flex',
  flexWrap: 'wrap' as const,
  gap: 10,
};

const buildSegmentedControlStyle = (
  active: boolean,
  compact: boolean,
  accent?: Record<string, string>
) => ({
  borderRadius: 999,
  border: active ? '1px solid transparent' : '1px solid var(--loyalty-border)',
  minHeight: compact ? 36 : 42,
  padding: compact ? '7px 12px' : '10px 16px',
  fontSize: compact ? 13 : 14,
  fontWeight: 700,
  background: active ? accent?.backgroundColor ?? '#2563eb' : 'var(--loyalty-surface-alt)',
  color: active ? accent?.color ?? '#ffffff' : 'var(--loyalty-text)',
  boxShadow: active ? '0 10px 24px rgba(15, 23, 42, 0.10)' : 'none',
});

const schedulePanelStyle = {
  marginTop: 14,
  padding: 14,
  borderRadius: 14,
  border: '1px solid var(--loyalty-border)',
  background: 'var(--loyalty-surface-alt)',
};

const getScheduleTogglePanelStyle = (enabled: boolean) => ({
  borderRadius: 10,
  padding: '8px 10px',
  border: `1px solid ${enabled ? 'var(--loyalty-accent-border)' : 'var(--loyalty-border)'}`,
  background: enabled ? 'var(--loyalty-accent-soft)' : 'var(--loyalty-surface-alt)',
  boxShadow: enabled ? 'inset 0 0 0 1px rgba(59, 130, 246, 0.08)' : 'none',
});

const innerPanelStyle = {
  borderRadius: 14,
  border: '1px solid var(--loyalty-border)',
  background: 'var(--loyalty-surface)',
  padding: 14,
};

const innerPanelTitleStyle = {
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: 'var(--loyalty-text-muted)',
  marginBottom: 10,
};

const plainPricingModeLabelStyle = {
  minHeight: 42,
  display: 'inline-flex',
  alignItems: 'center',
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: 'var(--loyalty-heading)',
  fontSize: 14,
  fontWeight: 700,
  boxShadow: 'none',
};

const actionButtonStyle = {
  fontWeight: 700,
};

const addRewardButtonStyle = {
  ...actionButtonStyle,
  backgroundColor: 'var(--loyalty-info-soft)',
  borderColor: 'var(--loyalty-info-border)',
  color: 'var(--loyalty-info-text)',
};

const cancelButtonStyle = {
  ...actionButtonStyle,
  backgroundColor: 'var(--loyalty-surface)',
  borderColor: 'var(--loyalty-border)',
  color: 'var(--loyalty-text)',
};

const saveButtonStyle = {
  ...actionButtonStyle,
  backgroundColor: '#2563eb',
  borderColor: '#2563eb',
  color: '#ffffff',
  minWidth: 140,
};

const simpleInfoChipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '6px 10px',
  borderRadius: 999,
  background: 'var(--loyalty-accent-soft)',
  border: '1px solid var(--loyalty-accent-border)',
  color: 'var(--loyalty-accent-text)',
  fontSize: 12,
  fontWeight: 700,
};

type CardModalProps = {
  show: boolean;
  editingCardId: string | null;
  cardKind: LoyaltyCampaignCardKind;
  cardName: string;
  cardGroupId: string;
  cardGroupName: string;
  cardGroupDisplayOrder: string;
  cardSubgroupName: string;
  subgroupNameOptions: string[];
  getSubgroupNameOptionLabel: (value: string) => string;
  cardCost: string;
  cardItemBaseStoreOfferId: string;
  cardItemSKUPrice: string;
  cardItemBonusLoyaltyPoints: string;
  cardClaimType: LoyaltyCampaignClaimMode;
  cardMaxClaims: string;
  cardUseSchedule: boolean;
  cardStartDate: Date | null;
  cardEndDate: Date | null;
  cardRewards: RewardFormInput[];
  activeItemCategory: LoyaltyCampaignItemCategory | null;
  activeVanityItemType: VanityItemType | null;
  directCurrencyRewardType: LevelRewardInputType | null;
  directCurrencyReward: RewardFormInput | null;
  directCurrencyAmount: number;
  offerOptions: LoyaltyCampaignOfferOptionRow[];
  offerPriceMap: Record<string, number>;
  offerSkuMap: Record<string, string[]>;
  skuPriceMap: Record<string, number>;
  hasValidCardInput: boolean;
  createCardLoading: boolean;
  updateCardLoading: boolean;
  editingRewardIndex: number | null;
  onHide: () => void;
  onSaveCard: () => void;
  onOpenAddRewardModal: () => void;
  onOpenEditRewardModal: (index: number) => void;
  onChangeDirectCurrencyAmount: (rawAmount: string) => void;
  setCardName: (value: string) => void;
  setCardCost: (value: string) => void;
  setCardSubgroupName: (value: string) => void;
  setCardItemBaseStoreOfferId: (value: string) => void;
  setCardItemSKUPrice: (value: string) => void;
  setCardItemBonusLoyaltyPoints: (value: string) => void;
  setCardClaimType: (value: LoyaltyCampaignClaimMode) => void;
  setCardMaxClaims: (value: string) => void;
  setCardUseSchedule: (value: boolean) => void;
  setCardStartDate: (value: Date | null) => void;
  setCardEndDate: (value: Date | null) => void;
  setCardRewards: (value: RewardFormInput[] | ((prev: RewardFormInput[]) => RewardFormInput[])) => void;
  setVanityItemTypePreset: (value: VanityItemType | null) => void;
  setEditingRewardIndex: (value: number | null) => void;
  setSelectedReward: (value: null) => void;
  setShowRewardModal: (value: boolean) => void;
};

function CardModal({
  show,
  editingCardId,
  cardKind,
  cardName,
  cardGroupId,
  cardGroupName,
  cardGroupDisplayOrder,
  cardSubgroupName,
  subgroupNameOptions,
  getSubgroupNameOptionLabel,
  cardCost,
  cardItemBaseStoreOfferId,
  cardItemSKUPrice,
  cardItemBonusLoyaltyPoints,
  cardClaimType,
  cardMaxClaims,
  cardUseSchedule,
  cardStartDate,
  cardEndDate,
  cardRewards,
  activeItemCategory,
  activeVanityItemType,
  directCurrencyRewardType,
  directCurrencyReward,
  directCurrencyAmount,
  offerOptions,
  offerPriceMap,
  offerSkuMap,
  skuPriceMap,
  hasValidCardInput,
  createCardLoading,
  updateCardLoading,
  editingRewardIndex,
  onHide,
  onSaveCard,
  onOpenAddRewardModal,
  onOpenEditRewardModal,
  onChangeDirectCurrencyAmount,
  setCardName,
  setCardCost,
  setCardSubgroupName,
  setCardItemBaseStoreOfferId,
  setCardItemSKUPrice,
  setCardItemBonusLoyaltyPoints,
  setCardClaimType,
  setCardMaxClaims,
  setCardUseSchedule,
  setCardStartDate,
  setCardEndDate,
  setCardRewards,
  setVanityItemTypePreset,
  setEditingRewardIndex,
  setSelectedReward,
  setShowRewardModal,
}: CardModalProps) {
  const [viewportHeight, setViewportHeight] = useState<number>(
    typeof window === 'undefined' ? 900 : window.innerHeight
  );
  const rewardCount = directCurrencyRewardType ? (directCurrencyReward ? 1 : 0) : cardRewards.length;
  const isCompact = viewportHeight < 860;
  const isVeryCompact = viewportHeight < 760;
  const bodyPadding = isVeryCompact ? 12 : isCompact ? 16 : 20;
  const cardPadding = isVeryCompact ? 12 : isCompact ? 14 : 18;
  const verticalGap = isVeryCompact ? 12 : 16;
  const fieldGap = isVeryCompact ? 2 : 3;
  const sectionCardStyle = useMemo(
    () => ({
      borderRadius: 18,
      border: '1px solid var(--loyalty-border)',
      background: 'var(--loyalty-surface)',
      padding: cardPadding,
      boxShadow: 'var(--loyalty-shadow)',
    }),
    [cardPadding]
  );
  const compactSectionTitleStyle = useMemo(
    () => ({
      ...sectionTitleStyle,
      marginBottom: isCompact ? 10 : 14,
    }),
    [isCompact]
  );
  const compactInnerPanelStyle = useMemo(
    () => ({
      ...innerPanelStyle,
      padding: isCompact ? 12 : 14,
    }),
    [isCompact]
  );
  const compactSchedulePanelStyle = useMemo(
    () => ({
      ...schedulePanelStyle,
      marginTop: isCompact ? 10 : 14,
      padding: isCompact ? 12 : 14,
    }),
    [isCompact]
  );
  const localTimezone =
    typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'Local Time';

  const formatLocalDateTime = (value: Date | null): string | null => {
    if (!value) {
      return null;
    }

    return value.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    });
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!show) {
      return;
    }
    if (cardItemSKUPrice) {
      setCardItemSKUPrice('');
    }
    if (cardItemBaseStoreOfferId) {
      setCardItemBaseStoreOfferId('');
    }
  }, [
    show,
    editingCardId,
    cardItemBaseStoreOfferId,
    cardItemSKUPrice,
    setCardItemBaseStoreOfferId,
    setCardItemSKUPrice,
  ]);

  const itemTitle =
    activeItemCategory === 'VANITY'
      ? 'Vanity Item'
      : activeItemCategory
        ? LOYALTY_CAMPAIGN_ITEM_CATEGORY_LABELS[activeItemCategory]
        : 'Item';
  const isGroupManagedItem = cardKind === 'ITEM' && cardGroupId.trim().length > 0;
  const isSimpleGroupedItemModal = isGroupManagedItem;
  const modalTitle = editingCardId
    ? `Edit ${cardKind === 'ITEM' ? 'Item' : 'Bundle Card'}`
    : `Add ${cardKind === 'ITEM' ? 'Item' : 'Bundle Card'}`;

  return (
    <Modal show={show} onHide={onHide} centered size="xl" className="loyalty-campaign-modal">
      <Modal.Header closeButton style={{ padding: isCompact ? '12px 16px' : undefined }}>
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="loyalty-campaign-modal-body" style={{ ...modalBodyStyle, padding: bodyPadding }}>
        <div className="d-flex flex-column" style={{ gap: verticalGap }}>
          {(cardKind !== 'ITEM' || activeItemCategory === 'VANITY' || !(activeItemCategory === 'COINS' || activeItemCategory === 'DIAMONDS')) && (
            <div className="loyalty-campaign-modal-section" style={sectionCardStyle}>
              {cardKind === 'ITEM' && activeItemCategory === 'VANITY' ? (
                <div className="mb-3">
                  <Form.Label className="mb-2 d-block">Vanity Type</Form.Label>
                  <div style={segmentedWrapStyle}>
                    {(['VINYL', 'FRAME', 'STICKER'] as VanityItemType[]).map((vanityType) => (
                      <button
                        key={vanityType}
                        type="button"
                        style={buildSegmentedControlStyle(activeVanityItemType === vanityType, isCompact)}
                        onClick={() => {
                          if (
                            cardRewards.length > 0 &&
                            getVanityItemTypeFromRewardForm(cardRewards[0]) !== vanityType
                          ) {
                            setCardRewards([]);
                          }
                          setVanityItemTypePreset(vanityType);
                        }}
                      >
                        {VANITY_ITEM_LABELS[vanityType]}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
              {!(cardKind === 'ITEM' && (activeItemCategory === 'COINS' || activeItemCategory === 'DIAMONDS')) ? (
                <Row className={`g-${fieldGap} mt-1`}>
                  <Col>
                    <Form.Label className="mb-1">{isSimpleGroupedItemModal ? 'Item Name' : 'Card Name'}</Form.Label>
                    <Form.Control
                      placeholder={isSimpleGroupedItemModal ? 'Item name' : 'Name'}
                      value={cardName}
                      onChange={(e) => setCardName(e.target.value)}
                    />
                  </Col>
                </Row>
              ) : null}
              {isGroupManagedItem ? (
                <div className="mt-3 loyalty-campaign-modal-panel" style={compactInnerPanelStyle}>
                  <div style={innerPanelTitleStyle}>Placement</div>
                  <div className="d-flex flex-wrap mb-3" style={{ gap: 8 }}>
                    <span style={simpleInfoChipStyle}>Group: {cardGroupName}</span>
                    {cardStartDate ? (
                      <span style={simpleInfoChipStyle}>{formatLocalDateTime(cardStartDate)}</span>
                    ) : (
                      <span style={simpleInfoChipStyle}>Always active</span>
                    )}
                    {cardEndDate ? (
                      <span style={simpleInfoChipStyle}>Until {formatLocalDateTime(cardEndDate)}</span>
                    ) : null}
                  </div>
                  <Form.Group className="mb-0">
                    <Form.Label className="mb-1">Subgroup</Form.Label>
                    <Form.Control
                      as="select"
                      value={cardSubgroupName}
                      onChange={(e) => setCardSubgroupName(e.target.value)}
                    >
                      {Array.from(new Set([cardSubgroupName, ...subgroupNameOptions].filter(Boolean))).map((option) => (
                        <option key={option} value={option}>
                          {getSubgroupNameOptionLabel(option)}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                  <Form.Text muted>
                    Item schedule follows the subgroup override when set,
                    otherwise it inherits the parent group schedule.
                  </Form.Text>
                </div>
              ) : null}
            </div>
          )}

          <div className="loyalty-campaign-modal-section" style={sectionCardStyle}>
            <div style={compactSectionTitleStyle}>{isSimpleGroupedItemModal ? 'Item Setup' : 'Pricing & Limits'}</div>
            <Row className={`g-${fieldGap}`}>
              <Col md={6}>
                <div className="loyalty-campaign-modal-panel" style={compactInnerPanelStyle}>
                  <div style={innerPanelTitleStyle}>{isSimpleGroupedItemModal ? 'Price' : 'Pricing'}</div>
                  {!isSimpleGroupedItemModal ? (
                    <div className="mb-3">
                      <Form.Label className="mb-2 d-block">Pricing Mode</Form.Label>
                      <div style={segmentedWrapStyle}>
                        <button
                          type="button"
                          style={plainPricingModeLabelStyle}
                        >
                          Loyalty Points Only
                        </button>
                      </div>
                    </div>
                  ) : null}
                  <Form.Group className={isSimpleGroupedItemModal ? 'mb-0' : 'mb-3'}>
                    <Form.Label className="mb-1">Loyalty Point Cost</Form.Label>
                    <Form.Control
                      placeholder="Enter loyalty point cost"
                      type="number"
                      value={cardCost}
                      onChange={(e) => setCardCost(e.target.value)}
                    />
                  </Form.Group>
                </div>
              </Col>
              <Col md={6}>
                <div className="loyalty-campaign-modal-panel" style={compactInnerPanelStyle}>
                  <div style={innerPanelTitleStyle}>{isSimpleGroupedItemModal ? 'Claim' : 'Claim Rules'}</div>
                  <Form.Group className={cardClaimType === 'CUSTOM' ? 'mb-3' : 'mb-0'}>
                    <Form.Label className="mb-1">Claim Type</Form.Label>
                    <Form.Control
                      as="select"
                      value={cardClaimType}
                      onChange={(e) => setCardClaimType(e.currentTarget.value as LoyaltyCampaignClaimMode)}
                    >
                      <option value="ONE_TIME">ONE_TIME</option>
                      <option value="INFINITE">INFINITE</option>
                      <option value="CUSTOM">CUSTOM</option>
                    </Form.Control>
                  </Form.Group>
                  {cardClaimType === 'CUSTOM' ? (
                    <Form.Group className="mb-0">
                      <Form.Label className="mb-1">Max Claims</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        value={cardMaxClaims}
                        placeholder="Enter max claims"
                        onChange={(e) => setCardMaxClaims(e.target.value)}
                      />
                      <Form.Text muted>Set how many times each user can claim this card.</Form.Text>
                    </Form.Group>
                  ) : null}
                </div>
              </Col>
            </Row>
            {isSimpleGroupedItemModal ? (
              <div className="mt-3 text-muted" style={{ fontSize: 13 }}>
                Schedule follows the parent group.
              </div>
            ) : null}
          </div>

          <div className="loyalty-campaign-modal-section" style={sectionCardStyle}>
            <div style={compactSectionTitleStyle}>{isSimpleGroupedItemModal ? 'Reward' : 'Rewards & Schedule'}</div>
            {isGroupManagedItem && !isSimpleGroupedItemModal ? (
              <div className="loyalty-campaign-modal-panel" style={compactSchedulePanelStyle}>
                <div className="d-flex align-items-center flex-wrap justify-content-between" style={{ gap: 8 }}>
                  <div>
                    <div className="font-weight-bold loyalty-campaign-heading">
                      Group Schedule
                    </div>
                    <div className="small text-muted mt-1">
                      Item schedule follows the group schedule and cannot be changed per item.
                    </div>
                  </div>
                  <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                    <span
                      className="loyalty-campaign-info-chip"
                      style={{
                        borderRadius: 999,
                        padding: '6px 10px',
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        color: '#1d4ed8',
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {cardStartDate ? `${formatLocalDateTime(cardStartDate)} (${localTimezone})` : 'Always active'}
                    </span>
                    {cardEndDate ? (
                      <span
                        className="loyalty-campaign-info-chip"
                        style={{
                          borderRadius: 999,
                          padding: '6px 10px',
                          background: '#eff6ff',
                          border: '1px solid #bfdbfe',
                          color: '#1d4ed8',
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        Until {formatLocalDateTime(cardEndDate)} ({localTimezone})
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : !isGroupManagedItem ? (
              <>
                <div
                  className="d-flex justify-content-between align-items-center flex-wrap"
                  style={{
                    gap: 12,
                    marginBottom: isCompact ? 10 : 12,
                    ...getScheduleTogglePanelStyle(cardUseSchedule),
                  }}
                >
                  <div>
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                      <Form.Check
                        type="switch"
                        id="card-use-schedule"
                        label="Use schedule"
                        checked={cardUseSchedule}
                        onChange={(e) => {
                          const enabled = e.currentTarget.checked;
                          setCardUseSchedule(enabled);
                          if (!enabled) {
                            setCardEndDate(null);
                          }
                        }}
                      />
                      {!cardUseSchedule ? (
                        <span
                          className="loyalty-campaign-success-chip"
                          style={{
                            borderRadius: 999,
                            padding: '6px 10px',
                            background: '#f0fdf4',
                            border: '1px solid #bbf7d0',
                            color: '#166534',
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          Always active
                        </span>
                      ) : null}
                    </div>
                    <div className="small text-muted mt-1">
                      Campaign status follows UTC. Local time is shown next to each field for reference.
                    </div>
                  </div>
                </div>

                {cardUseSchedule ? (
              <div className="loyalty-campaign-modal-panel" style={compactSchedulePanelStyle}>
                <Row className={`g-${fieldGap}`}>
                  <Col md={6}>
                    <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 8 }}>
                      <Form.Label className="mb-1 d-block">Start Date (UTC)</Form.Label>
                      {cardStartDate ? (
                        <span className="small text-muted">{`Local time: ${formatLocalDateTime(cardStartDate)} (${localTimezone})`}</span>
                      ) : null}
                    </div>
                      <DateTimePicker
                      dateFormat="YYYY-MM-DD"
                      utc={true}
                      timeFormat="HH:mm"
                      timeConstraints={{ minutes: { min: 0, max: 59, step: 1 } }}
                      onChange={(value) => {
                        const nextDate = toPickerDate(value);
                        if (nextDate) {
                          setCardStartDate(nextDate);
                        }
                      }}
                      value={cardStartDate ?? undefined}
                    />
                  </Col>
                  <Col md={6}>
                    <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 8 }}>
                      <Form.Label className="mb-1 d-block">End Date (UTC)</Form.Label>
                      {cardEndDate ? (
                        <span className="small text-muted">{`Local time: ${formatLocalDateTime(cardEndDate)} (${localTimezone})`}</span>
                      ) : null}
                    </div>
                    <DateTimePicker
                      dateFormat="YYYY-MM-DD"
                      utc={true}
                      timeFormat="HH:mm"
                      timeConstraints={{ minutes: { min: 0, max: 59, step: 1 } }}
                      inputProps={{ placeholder: 'Optional' }}
                      onChange={(value) => setCardEndDate(toPickerDate(value))}
                      value={cardEndDate ?? undefined}
                    />
                  </Col>
                </Row>
              </div>
                ) : null}
              </>
            ) : null}

            <div
              className="loyalty-campaign-modal-surface-card"
              style={{
                border: '1px solid #dbe4ee',
                borderRadius: 16,
                overflow: 'hidden',
                background: '#fbfdff',
                marginTop: isCompact ? 10 : 14,
              }}
            >
              <div
                className="d-flex justify-content-between align-items-center flex-wrap loyalty-campaign-modal-surface-header"
                style={{
                  gap: 12,
                  padding: '14px 16px',
                  borderBottom: '1px solid #e5edf5',
                  background: '#f3f8ff',
                }}
              >
                <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                  <div className="font-weight-bold loyalty-campaign-heading">
                    Rewards
                  </div>
                  <span
                    className="loyalty-campaign-info-chip"
                    style={{
                      borderRadius: 999,
                      padding: '4px 10px',
                      background: '#dbeafe',
                      color: '#1d4ed8',
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {rewardCount}
                  </span>
                </div>
                {!directCurrencyRewardType && (cardKind === 'BUNDLE' || cardRewards.length === 0) ? (
                  <Button size="sm" variant="outline-primary" style={addRewardButtonStyle} onClick={onOpenAddRewardModal}>
                    Add Reward
                  </Button>
                ) : null}
              </div>
              <div style={{ padding: 16 }}>
                {cardKind === 'ITEM' && cardRewards[0] && (
                  <div className="mb-3 d-flex align-items-center" style={{ gap: 8 }}>
                    <span
                      className="badge badge-pill"
                      style={getItemCategoryBadgeStyle(getItemCategoryFromRewardForm(cardRewards[0]))}
                    >
                      {LOYALTY_CAMPAIGN_ITEM_CATEGORY_LABELS[getItemCategoryFromRewardForm(cardRewards[0])]}
                    </span>
                  </div>
                )}
                {directCurrencyRewardType ? (
                  <Row className={`g-${Math.max(1, fieldGap - 1)} align-items-end`}>
                    <Col md={activeItemCategory === 'COINS' ? 6 : 12}>
                      <Form.Label className="mb-1">
                        {activeItemCategory === 'COINS' ? '💰 Coin Amount' : '💎 Diamond Amount'}
                      </Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        placeholder={activeItemCategory === 'COINS' ? '1000' : '170'}
                        value={directCurrencyReward ? String(directCurrencyAmount) : ''}
                        onChange={(e) => onChangeDirectCurrencyAmount(e.target.value)}
                      />
                    </Col>
                    {activeItemCategory === 'COINS' && (
                      <Col md={6}>
                        <div className="h-100 d-flex align-items-center">
                          {directCurrencyReward ? (
                            <Chip
                              reward={fromRewardFormToLevelRewardInput(directCurrencyReward)}
                              globalAction={() => undefined}
                            />
                          ) : null}
                        </div>
                      </Col>
                    )}
                  </Row>
                ) : (
                  <>
                    {cardRewards.length === 0 ? (
                      <div
                        className="text-center text-muted loyalty-campaign-empty-state"
                        style={{
                          padding: '18px 16px',
                          border: '1px dashed #cbd5e1',
                          borderRadius: 14,
                          background: '#ffffff',
                        }}
                      >
                        Add a reward to complete this card.
                      </div>
                    ) : null}
                    <div className="d-flex flex-wrap" style={{ gap: 10 }}>
                      {cardRewards.map((reward, index) => (
                        <Chip
                          key={`${reward.type}_${reward.itemId}_${index}`}
                          reward={fromRewardFormToLevelRewardInput(reward)}
                          canDelete
                          globalAction={() => onOpenEditRewardModal(index)}
                          btnAction={() => {
                            setCardRewards((prev) => prev.filter((_, i) => i !== index));
                            if (editingRewardIndex === index) {
                              setEditingRewardIndex(null);
                              setSelectedReward(null);
                              setShowRewardModal(false);
                            }
                          }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer
        className="loyalty-campaign-modal-footer"
        style={{ borderTop: '1px solid var(--loyalty-border)', padding: isCompact ? '12px 16px' : '16px 20px' }}
      >
        <div className="mr-auto small text-muted">
          {rewardCount > 0 ? `${rewardCount} reward configured` : 'No rewards configured'}
        </div>
        <Button variant="outline-secondary" style={cancelButtonStyle} onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          style={saveButtonStyle}
          onClick={onSaveCard}
          disabled={!hasValidCardInput || createCardLoading || updateCardLoading}
        >
          {createCardLoading || updateCardLoading ? 'Saving...' : editingCardId ? 'Save Changes' : 'Create Card'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default CardModal;
